import React, { useState } from "react";
import styles from "./LeaveRequestModal.module.css";
import { toast } from "react-toastify";
import WebService from "../Services/WebService";
import CrossButton from "../Customs/Button/CrossButton";

interface LeaveRequestModalProps {
  onClose: () => void;
  onSuccess: () => void;
  availableDays?: number; // kept in props signature (unused), per your file
}

const LEAVE_TYPES = [
  { value: "SICK", label: "Sick Leave" },
  { value: "CASUAL", label: "Casual Leave" },
];

function todayDateStr() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

/** ---- Sunday-only rules (Saturday is allowed) ---- */
const isSunday = (d: Date) => d.getDay() === 0;

const daysExcludingSunday = (fromStr: string, toStr: string): number => {
  const from = new Date(fromStr);
  const to = new Date(toStr);
  if (isNaN(+from) || isNaN(+to) || to < from) return 0;

  let count = 0;
  const cur = new Date(from);
  while (cur <= to) {
    if (!isSunday(cur)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const rangeHasSunday = (fromStr: string, toStr: string): boolean => {
  const from = new Date(fromStr);
  const to = new Date(toStr);
  if (isNaN(+from) || isNaN(+to) || to < from) return false;

  const cur = new Date(from);
  while (cur <= to) {
    if (isSunday(cur)) return true;
    cur.setDate(cur.getDate() + 1);
  }
  return false;
};

const LeaveRequestModal: React.FC<LeaveRequestModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [leaveType, setLeaveType] = useState(LEAVE_TYPES[0].value);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  // Validation flags
  const isFromPast = fromDate && new Date(fromDate) < new Date(todayDateStr());
  const isToPast = toDate && new Date(toDate) < new Date(todayDateStr());
  const isRangeInvalid =
    fromDate && toDate && new Date(toDate) < new Date(fromDate);

  // Days logic (exclude Sundays)
  const daysRequested =
    fromDate && toDate ? daysExcludingSunday(fromDate, toDate) : 0;
  const hasSundayInRange =
    fromDate && toDate ? rangeHasSunday(fromDate, toDate) : false;

  // Reason (notes) validation
  const isNotesValid = notes.trim().length >= 8;

  // Enable submit only when all conditions pass
  const canSubmit =
    fromDate &&
    toDate &&
    !isFromPast &&
    !isToPast &&
    !isRangeInvalid &&
    !hasSundayInRange && // block ranges that include any Sunday
    daysRequested > 0 &&
    isNotesValid &&
    !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      toast.error("Please select both From and To dates.");
      return;
    }
    if (isFromPast) {
      toast.error("From date cannot be in the past.");
      return;
    }
    if (isToPast) {
      toast.error("To date cannot be in the past.");
      return;
    }
    if (isRangeInvalid) {
      toast.error("To date cannot be before From date.");
      return;
    }
    if (hasSundayInRange) {
      toast.error(
        "Selected range includes Sunday. Please split your leave (e.g., Fri–Sat, then Monday separately)."
      );
      return;
    }
    if (daysRequested <= 0) {
      toast.error("Number of days must be at least 1.");
      return;
    }
    if (!isNotesValid) {
      setNotesError("Please enter a valid reason (at least 8 characters).");
      return;
    }

    setLoading(true);
    setNotesError(null);
    try {
      await WebService.postAPI({
        action: "/v1/door-management/leave/apply",
        body: {
          leave_type: leaveType,
          start_date: new Date(fromDate).toISOString(),
          end_date: new Date(toDate).toISOString(),
          reason: notes,
        },
      });
      toast.success("Leave applied successfully!");
      onSuccess();
      onClose();
    } catch (err) {
      // toast.error("Failed to apply for leave.");
    }
    setLoading(false);
  };

  // Reset all on close
  const handleClose = () => {
    setLeaveType(LEAVE_TYPES[0].value);
    setFromDate("");
    setToDate("");
    setNotes("");
    setLoading(false);
    setNotesError(null);
    onClose();
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalBox}>
        <div className={styles.header}>
          <span>📝 Request leave</span>
          <CrossButton onClick={handleClose} className={styles.closeBtn} />
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Row 1: Leave type */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Select leave type</label>
              <select
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                className={styles.selectInput}
              >
                {LEAVE_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.sectionLabel}>Select dates</div>

          {/* Row 2: Dates */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>📅 From date</label>
              <input
                type="date"
                min={todayDateStr()}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={styles.dateInput}
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>📅 To date</label>
              <input
                type="date"
                min={fromDate || todayDateStr()}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={styles.dateInput}
                required
                disabled={!fromDate}
              />
            </div>
          </div>

          {/* Inline Sunday-range message (helps users split ranges) */}
          {fromDate && toDate && hasSundayInRange && (
            <div className={styles.errorMsg}>
              The selected range includes Sunday. Please split your leave
              (e.g., Fri–Sat, then Monday separately).
            </div>
          )}

          {/* Days summary chip (shows days excluding Sundays) */}
          {fromDate &&
            toDate &&
            !isRangeInvalid &&
            !isFromPast &&
            !isToPast &&
            !hasSundayInRange &&
            daysRequested > 0 && (
              <div className={styles.daysSummary}>
                <span className={styles.daysBadge}>{daysRequested}</span>
                <span className={styles.daysText}>
                  {daysRequested === 1 ? "day" : "days"}
                </span>
              </div>
            )}

          {/* Reason */}
          <div className={styles.formGroupFull}>
            <label>
              Reason <span style={{ color: "#e43e2d" }}>*</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                if (e.target.value.trim().length < 8) {
                  setNotesError(
                    "Please enter a valid reason (at least 8 characters)."
                  );
                } else {
                  setNotesError(null);
                }
              }}
              className={styles.textArea}
              placeholder="e.g., Doctor's note attached"
              rows={3}
            />
            {notesError && (
              <div className={styles.errorMsg}>{notesError}</div>
            )}
          </div>

          {/* Actions */}
          <div className={styles.actionRow}>
            <button
              type="submit"
              className={styles.applyBtn}
              disabled={!canSubmit}
            >
              {loading ? "Applying..." : "Apply"}
            </button>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={handleClose}
            >
              Cancel
            </button>
          </div>

          {/* Inline validation messages */}
          {isFromPast && (
            <div className={styles.errorMsg}>
              From date cannot be in the past.
            </div>
          )}
          {isToPast && (
            <div className={styles.errorMsg}>
              To date cannot be in the past.
            </div>
          )}
          {isRangeInvalid && (
            <div className={styles.errorMsg}>
              To date cannot be before From date.
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default LeaveRequestModal;
